import * as React from 'react';
import {
    makeStyles,
    shorthands,
    tokens,
    mergeClasses,
} from '@fluentui/react-components';

// Define the properties for our custom Stack component
interface StackProps extends React.HTMLAttributes<HTMLDivElement> {
    /**
     * Defines if the Stack should be a horizontal (row) or vertical (column) flex container.
     * Default is vertical (column).
     */
    horizontal?: boolean;
    /**
     * Defines how items are aligned along the main axis of the flex container.
     */
    horizontalAlign?: 'start' | 'center' | 'end' | 'space-between' | 'space-around' | 'space-evenly';
    /**
     * Defines how items are aligned along the cross axis of the flex container.
     */
    verticalAlign?: 'start' | 'center' | 'end' | 'stretch' | 'baseline';
    /**
     * Defines whether flex items are forced into a single line or can wrap onto multiple lines.
     */
    wrap?: boolean;
    /**
     * Defines the gap between items. Uses Fluent UI design tokens for consistent spacing.
     * Can be a single spacing token string (e.g., "gapBase200") or a number for px.
     */
    gap?: keyof typeof tokens | string | number;
    /**
     * Children to be rendered inside the Stack.
     */
    children?: React.ReactNode;
}

// Fluent UI v9 useStyles hook
const useStyles = makeStyles({
    root: {
        display: 'flex',
        // Default to vertical (column)
        flexDirection: 'column',
        boxSizing: 'border-box', // Ensure padding and border are included in the element's total width and height
    },
    horizontal: {
        flexDirection: 'row',
    },
    wrap: {
        flexWrap: 'wrap',
    },

    // Horizontal alignment (justify-content)
    hAlignStart: {
        justifyContent: 'flex-start',
    },
    hAlignCenter: {
        justifyContent: 'center',
    },
    hAlignEnd: {
        justifyContent: 'flex-end',
    },
    hAlignSpaceBetween: {
        justifyContent: 'space-between',
    },
    hAlignSpaceAround: {
        justifyContent: 'space-around',
    },
    hAlignSpaceEvenly: {
        justifyContent: 'space-evenly',
    },

    // Vertical alignment (align-items)
    vAlignStart: {
        alignItems: 'flex-start',
    },
    vAlignCenter: {
        alignItems: 'center',
    },
    vAlignEnd: {
        alignItems: 'flex-end',
    },
    vAlignStretch: {
        alignItems: 'stretch',
    },
    vAlignBaseline: {
        alignItems: 'baseline',
    },

    // Dynamic gap styling
    gap: {
        // This style will be applied dynamically
    },
});

export const Stack: React.FC<StackProps> = ({
    horizontal = false,
    horizontalAlign,
    verticalAlign,
    wrap = false,
    gap,
    children,
    className, // Allow external classes to be passed
    ...rest // Capture any other div props
}) => {
    const styles = useStyles();

    // Determine the flex direction class
    const directionClass = horizontal ? styles.horizontal : null;

    // Determine the wrap class
    const wrapClass = wrap ? styles.wrap : null;

    // Determine horizontal alignment class
    const hAlignClass = horizontalAlign && {
        'start': styles.hAlignStart,
        'center': styles.hAlignCenter,
        'end': styles.hAlignEnd,
        'space-between': styles.hAlignSpaceBetween,
        'space-around': styles.hAlignSpaceAround,
        'space-evenly': styles.hAlignSpaceEvenly,
    }[horizontalAlign];

    // Determine vertical alignment class
    const vAlignClass = verticalAlign && {
        'start': styles.vAlignStart,
        'center': styles.vAlignCenter,
        'end': styles.vAlignEnd,
        'stretch': styles.vAlignStretch,
        'baseline': styles.vAlignBaseline,
    }[verticalAlign];

    // Dynamic gap styling using React.useMemo for efficiency
    // This approach creates a style object on the fly for the gap,
    // as makeStyles doesn't directly support dynamic CSS variables or calculation based on props.
    const gapStyle = React.useMemo(() => {
        if (gap === undefined) {
            return {};
        }

        let gapValue: string;
        if (typeof gap === 'number') {
            gapValue = `${gap}px`;
        } else if (typeof tokens[gap as keyof typeof tokens] === 'string') {
            // If gap is a key of tokens (e.g., "spacingHorizontalM")
            gapValue = tokens[gap as keyof typeof tokens] as string;
        } else {
            // Assume it's already a CSS string like "10px" or "1rem"
            gapValue = gap;
        }

        return {
            gap: gapValue,
        };
    }, [gap]);


    // Merge all applicable class names
    const rootClasses = mergeClasses(
        styles.root,
        directionClass,
        wrapClass,
        hAlignClass,
        vAlignClass,
        className // Include any external classes passed in
    );

    return (
        <div className={rootClasses} style={gapStyle} {...rest}>
            {children}
        </div>
    );
};